import type { SavedSearchInput } from '@finn-no/saved-search-settings-component';
import { i18n } from '@lingui/core';
import { Button, Modal } from '@warp-ds/react';
import React from 'react';
import type { SavedSearchData } from '../../../server';
import { useAppConfig } from '../../AppConfig';
import { useCsrfToken } from '../useCsrfToken';
import { useScrollLockFix } from './ScrollLockFix';

const modalStyle: React.CSSProperties = {
    // @ts-expect-error Object literal may only specify known properties. ts(2322)
    '--w-modal-width': '420px',
};

type UpdateSavedSearchModalProps = {
    isModified: boolean;
    isOpen: boolean;
    onDismiss: () => void;
    onError: (error: unknown) => void;
    onSave: (id?: number) => void;
    onSaveAsNew: () => void;
    savedSearchData: SavedSearchData;
    savedSearchInput: SavedSearchInput;
};

export function UpdateSavedSearchModal(props: UpdateSavedSearchModalProps) {
    const {
        isModified,
        isOpen,
        onDismiss,
        onError,
        onSave,
        onSaveAsNew,
        savedSearchData,
        savedSearchInput,
    } = props;

    const { apiUrl, isProbablyMobile } = useAppConfig();
    const csrfToken = useCsrfToken();

    const [isSaving, setIsSaving] = React.useState(false);

    async function updateSearch() {
        setIsSaving(true);

        const updatedSavedSearchData: SavedSearchData = {
            ...savedSearchData,
            parameters: savedSearchInput.parameters,
        };

        const url = new URL(`${apiUrl}/saved-search`);
        const body = JSON.stringify(updatedSavedSearchData);

        try {
            const response = await fetch(url, {
                body,
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Csrf-Token': csrfToken,
                },
                method: 'PUT',
            });

            if (response.ok) {
                onSave(savedSearchData.id);
            } else {
                onError(
                    new Error(`Got status ${response.status} calling ${url}`),
                );
            }
        } catch (err) {
            onError(err);
        } finally {
            onDismiss();
        }
    }

    useScrollLockFix();

    return (
        <Modal
            onDismiss={onDismiss}
            open={isOpen}
            style={!isProbablyMobile ? modalStyle : undefined}
            title={i18n.t({ id: 'saved-search.title', message: 'Save search' })}
        >
            <div className="flex flex-col space-y-16 py-4">
                <p>
                    {isModified
                        ? i18n.t({
                              id: 'saved-search.update.modified.yes',
                              message:
                                  'You have made changes to this saved search',
                          })
                        : i18n.t({
                              id: 'saved-search.update.modified.no',
                              message:
                                  'You have already saved this search named',
                          })}
                    &nbsp;«<strong>{savedSearchData.description}</strong>»
                </p>
                {isModified && (
                    <Button
                        fullWidth
                        loading={isSaving}
                        onClick={updateSearch}
                        primary
                    >
                        {i18n.t({
                            id: 'saved-search.update.save-changes',
                            message: 'Save changes',
                        })}
                    </Button>
                )}
                <Button fullWidth onClick={onSaveAsNew} primary={!isModified}>
                    {i18n.t({
                        id: 'saved-search.update.save-new',
                        message: 'Save as new search',
                    })}
                </Button>
                <Button fullWidth onClick={onDismiss} quiet>
                    {i18n.t({
                        id: 'saved-search.update.cancel',
                        message: 'Cancel',
                    })}
                </Button>
            </div>
        </Modal>
    );
}
